import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Download, ArrowRight, BookOpen, Home, User, CheckCircle, FileText, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const kitItems = [
  "Semaglutide vs Tirzepatide — Side-by-Side Comparison",
  "First 30 Days — Week by Week Breakdown",
  "Side Effect Survival Guide — Simple Fixes",
  "7-Day Meal Plan — High Protein, Easy Digestion",
  "GLP-1 Grocery List — What to Buy and What to Avoid",
  "Injection + Progress Tracker — 12 Weeks",
  "Results Timeline — Realistic Expectations",
  "Maximize Your Results — Habits That Accelerate Fat Loss",
  "Common Questions — Answered Honestly",
];

const StarterKitPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and email.');
      return;
    }
    setSubmitting(true);

    // Trigger download immediately (must be synchronous from user gesture)
    const link = document.createElement('a');
    link.href = '/downloads/GLP1_Starter_Kit-PG11_copy.pdf';
    link.download = 'GLP1_Starter_Kit.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Mark opt-in so GLP-1 exit popup won't show
    sessionStorage.setItem('starter_kit_submitted', '1');

    // Then save lead in background
    try {
      await fetch(`${API_URL}/api/starter-kit-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
    } catch {
      // Lead tracking is non-critical — download already completed
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Free GLP-1 Starter Kit | MyWeightForecast.com</title>
        <meta name="description" content="Download the free 12-page GLP-1 Starter Kit — medication comparison, meal plan, side effect guide, tracker, and more." />
      </Helmet>

      <div className="min-h-screen bg-[#0B1F3A] text-white" style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
        {/* Nav */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-[#0B1F3A]/90 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-[#2ABFBF]/10 border border-[#2ABFBF]/30 flex items-center justify-center">
                <span className="text-lg font-bold text-[#2ABFBF]">B</span>
              </div>
              <span className="text-xl font-bold text-white">Bryan 2.0</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-[#C8E8E8] hover:text-[#2ABFBF] transition-colors flex items-center gap-2">
                <Home className="h-4 w-4" /><span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/blog" className="text-[#C8E8E8] hover:text-[#2ABFBF] transition-colors flex items-center gap-2">
                <BookOpen className="h-4 w-4" /><span className="hidden sm:inline">Blog</span>
              </Link>
              <Link to="/about" className="text-[#C8E8E8] hover:text-[#2ABFBF] transition-colors flex items-center gap-2">
                <User className="h-4 w-4" /><span className="hidden sm:inline">About</span>
              </Link>
            </nav>
          </div>
        </header>

        {/* SECTION 1 — HERO */}
        <section className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#2ABFBF] opacity-[0.07] blur-[100px] rounded-full pointer-events-none" />
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-block mb-6 px-5 py-2 rounded-full bg-[#2ABFBF]/10 border border-[#2ABFBF]/30 text-[#2ABFBF] text-sm font-semibold tracking-wider" data-testid="starter-kit-badge">
                FREE DOWNLOAD
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white" data-testid="starter-kit-headline">
                Get the Free GLP-1 Starter Kit
              </h1>
              <p className="text-xl sm:text-2xl text-[#2ABFBF] font-semibold mb-6">
                What to Expect. What to Eat. How to Maximize Results.
              </p>
              <p className="text-[#C8E8E8] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Everything you need to start your GLP-1 journey with confidence — from your first injection to your first 90 days and beyond. No fluff. Just the honest guide most people never get.
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — WHAT'S INSIDE */}
        <section className="px-4 sm:px-6 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto rounded-2xl bg-[#2ABFBF]/10 border border-[#2ABFBF]/20 p-8 sm:p-10"
            data-testid="whats-inside-card"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">What's Inside This Free Kit:</h2>
            <ul className="space-y-4">
              {kitItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="h-5 w-5 text-[#2ABFBF] mt-0.5 flex-shrink-0" />
                  <span className="text-[#C8E8E8] text-base">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* SECTION 3 — OPT-IN FORM */}
        <section className="px-4 sm:px-6 pb-16" id="download-form">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-lg mx-auto rounded-2xl bg-[#0f2d4f] border border-white/10 p-8 sm:p-10"
            data-testid="starter-kit-form-card"
          >
            {submitted ? (
              <div className="text-center" data-testid="starter-kit-thank-you">
                <CheckCircle className="h-16 w-16 text-[#2ABFBF] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-3">You're In!</h3>
                <p className="text-[#C8E8E8] mb-6">Your download should start automatically. If not, click below.</p>
                <a
                  href="/downloads/GLP1_Starter_Kit-PG11_copy.pdf"
                  download="GLP1_Starter_Kit.pdf"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#2ABFBF] text-[#0B1F3A] font-bold text-base hover:bg-[#24a8a8] transition-colors"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-5 w-5" />
                  Download the Starter Kit
                </a>
                <p className="text-slate-500 text-xs mt-6">Also — want a personalized forecast?</p>
                <Link to="/quiz" className="text-[#2ABFBF] hover:underline text-sm font-semibold mt-1 inline-block">
                  Get My Free AI Forecast →
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="starter-kit-form">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">Get Your Free Kit</h3>
                <div className="space-y-4 mb-6">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="starter-kit-name"
                    className="w-full px-5 py-4 rounded-lg bg-[#0B1F3A] border border-white/15 text-white placeholder-slate-500 text-base focus:outline-none focus:border-[#2ABFBF]/60 transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="starter-kit-email"
                    className="w-full px-5 py-4 rounded-lg bg-[#0B1F3A] border border-white/15 text-white placeholder-slate-500 text-base focus:outline-none focus:border-[#2ABFBF]/60 transition-colors"
                  />
                </div>
                {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="starter-kit-submit"
                  className="w-full py-4 rounded-full bg-[#2ABFBF] text-[#0B1F3A] font-bold text-lg hover:bg-[#24a8a8] transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Sending...' : 'Send Me the Free Kit →'}
                </button>
                <p className="text-slate-500 text-xs text-center mt-4">No spam. Just your free guide. Unsubscribe anytime.</p>
              </form>
            )}
          </motion.div>
        </section>

        {/* SECTION 4 — TRUST STRIP */}
        <section className="px-4 sm:px-6 pb-16">
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 text-center" data-testid="trust-strip">
            <div className="flex flex-col items-center gap-2">
              <FileText className="h-8 w-8 text-[#2ABFBF]" />
              <span className="text-white font-bold text-lg">11 Pages</span>
              <span className="text-[#C8E8E8] text-sm">of Actionable Guidance</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8 text-[#2ABFBF]" />
              <span className="text-white font-bold text-lg">100% Free</span>
              <span className="text-[#C8E8E8] text-sm">No Credit Card Required</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <CheckCircle className="h-8 w-8 text-[#2ABFBF]" />
              <span className="text-white font-bold text-lg">Created by Bryan</span>
              <span className="text-[#C8E8E8] text-sm">at MyWeightForecast.com</span>
            </div>
          </div>
        </section>

        {/* SECTION 5 — CREATED BY BRYAN */}
        <section className="px-4 sm:px-6 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto rounded-2xl bg-[#0f2d4f] border border-[#2ABFBF]/15 p-8 sm:p-10"
            data-testid="created-by-bryan"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 rounded-full bg-[#2ABFBF]" />
              <h3 className="text-xl font-bold text-white">Created by Bryan</h3>
            </div>
            <p className="text-[#C8E8E8] leading-relaxed">
              I built this guide because I kept seeing people start GLP-1 programs without a real roadmap. No clear expectations. No meal guidance. No honest answers. This kit is everything I wish people had from day one — simple, honest, and actually useful.
            </p>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Bryan 2.0 Forecast. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-[#2ABFBF] transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-[#2ABFBF] transition-colors">Blog</Link>
              <Link to="/quiz" className="hover:text-[#2ABFBF] transition-colors">Take Quiz</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default StarterKitPage;
